import { z } from "zod/v4";
import { namedSocialIdSchema } from "@/features/client-metadata/schema";
import type { SocialUrlRules } from "@/features/client-metadata/schema";

const defaultRules: SocialUrlRules = { allowedProtocols: ["http:", "https:"], defaultProtocol: "https:", allowCredentials: false };
// Mirrors the API's http-url helper, including bare domains with a port.
export function normalizeSocialUrl(value: string, rules: SocialUrlRules = defaultRules) {
  const trimmed = value.trim();
  if (!trimmed) return null;
  let candidate = trimmed;
  if (trimmed.startsWith("//")) candidate = `${rules.defaultProtocol}${trimmed}`;
  else if (!/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed) || /^(?:localhost|[a-zA-Z\d-]+(?:\.[a-zA-Z\d-]+)+):\d+(?:[/?#]|$)/.test(trimmed)) candidate = `${rules.defaultProtocol}//${trimmed}`;
  try {
    const url = new URL(candidate);
    if (!rules.allowedProtocols.some((protocol) => protocol === url.protocol) || !url.hostname || url.username || url.password) return null;
    return url.href;
  } catch { return null; }
}
const url = z.string().trim().min(1, "Enter a URL.").refine((value) => normalizeSocialUrl(value) !== null, "Use an HTTP(S) URL without a username or password.");
export const socialLinkSchema = z.discriminatedUnion("id", [
  z.strictObject({ id: namedSocialIdSchema, url }),
  z.strictObject({ id: z.literal("custom"), platform: z.string().trim().min(1, "Enter a name."), url }),
]);
export const socialLinksSchema = z.array(socialLinkSchema).superRefine((links, ctx) => {
  const seen = new Set<string>();
  links.forEach((link, index) => {
    const key = link.id === "custom" ? `custom:${link.platform.toLowerCase()}` : link.id;
    if (seen.has(key)) ctx.addIssue({ code: "custom", path: [index, link.id === "custom" ? "platform" : "id"], message: "Use each platform once; custom names must be distinct." });
    seen.add(key);
  });
});
